import { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import checkGrayIcon from 'assets/check-gray.svg';
import copyChatIcon from 'assets/copy-chat.svg';

import copyValue from 'utlis/copy_value';

import styles from './styles.module.scss';

const IMPACT_CONFIG = {
  high: { color: '#EF2A35', label: 'High' },
  medium: { color: '#F0B100', label: 'Med' },
  med: { color: '#F0B100', label: 'Med' },
  low: { color: '#22C55E', label: 'Low' },
};

const normalizeImpact = impact => (impact ?? '').toLowerCase().trim();

const getScoreColor = value => {
  if (value > 40) {
    return '#22C14D';
  }

  if (value >= 0) {
    return '#FDC700';
  }

  return '#EF2A35';
};

const normalizeScore = rawScore => {
  if (rawScore == null) {
    return null;
  }

  let scoreDisplay = rawScore;

  if (typeof rawScore === 'string') {
    if (rawScore.trim() === '') {
      return null;
    }

    scoreDisplay = rawScore.trim().replace(/^\+/, '');
  }

  const scoreNumeric = Number(scoreDisplay);

  if (!isFinite(scoreNumeric)) {
    return null;
  }

  return {
    display: String(scoreDisplay).replace(/^\+/, ''),
    numeric: scoreNumeric,
  };
};

const buildCopyText = ({
  scoreDisplay, summary, areasOfImprovement, whatsWorking, primaryAudience, primaryMetrics,
}) => {
  const lines = ['Overview'];

  if (scoreDisplay != null) {
    lines.push(`${scoreDisplay} / 100 NPS`);
  }

  if (summary) {
    lines.push('', summary);
  }

  if (areasOfImprovement.length > 0) {
    lines.push('', 'Issues:');
    areasOfImprovement.forEach(row => {
      const impactLabel = IMPACT_CONFIG[normalizeImpact(row.impact)]?.label ?? row.impact;

      lines.push(`- ${row.issue}${impactLabel ? ` (${impactLabel})` : ''}`);

      if (row.what_to_improve) {
        lines.push(`  What to improve: ${row.what_to_improve}`);
      }
    });
  }

  if (whatsWorking.length > 0) {
    lines.push('', 'What\'s working:');
    whatsWorking.forEach(item => {
      lines.push(`- ${item}`);
    });
  }

  if (primaryAudience) {
    lines.push('', 'Primary audience:', primaryAudience);
  }

  if (primaryMetrics.length > 0) {
    lines.push('', 'Primary metrics:', primaryMetrics.join(' | '));
  }

  return lines.join('\n');
};

const OverviewReport = ({ overview }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapperRef = useRef(null);

  const {
    review_snippet: summary,
    areas_of_improvement: areasOfImprovement,
    whats_working: whatsWorking,
    primary_audience: primaryAudience,
    primary_metrics: primaryMetrics,
  } = overview || {};

  const hasImprovements = Array.isArray(areasOfImprovement) && areasOfImprovement.length > 0;
  const hasWhatsWorking = Array.isArray(whatsWorking) && whatsWorking.length > 0;
  const hasMetrics = Array.isArray(primaryMetrics) && primaryMetrics.length > 0;
  const hasCollapsibleContent = hasImprovements || hasWhatsWorking || Boolean(primaryAudience) || hasMetrics;
  const score = normalizeScore(overview?.score_assessment?.score);

  const copyText = useMemo(
    () => buildCopyText({
      scoreDisplay: score?.display,
      summary,
      areasOfImprovement: hasImprovements ? areasOfImprovement : [],
      whatsWorking: hasWhatsWorking ? whatsWorking : [],
      primaryAudience,
      primaryMetrics: hasMetrics ? primaryMetrics : [],
    }),
    [
      score?.display,
      summary,
      hasImprovements,
      areasOfImprovement,
      hasWhatsWorking,
      whatsWorking,
      primaryAudience,
      hasMetrics,
      primaryMetrics,
    ]
  );

  const handleCopy = () => {
    copyValue(copyText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles['overview-report']}>
      <h2 className={styles['overview-heading']}>
        Overview
      </h2>

      {score && (
        <div className={styles['nps-row']}>
          <span
            className={styles['nps-bar']}
            style={{ backgroundColor: getScoreColor(score.numeric) }}
          />
          <p className={styles['nps-value']}>
            <span className={styles['nps-score']}>
              {score.display}
            </span>
            <span className={styles['nps-separator']}>
              {' / '}
            </span>
            <span className={styles['nps-suffix']}>
              {'100 NPS'}
            </span>
          </p>
        </div>
      )}

      {summary && (
        <p className={styles['overview-summary']}>
          {summary}
        </p>
      )}

      {hasCollapsibleContent && (
        <div
          ref={wrapperRef}
          className={styles['collapsible-wrapper']}
          style={isExpanded && wrapperRef.current ? { maxHeight: wrapperRef.current.scrollHeight } : undefined}
        >
          <div className={styles['sections-container']}>
            {hasImprovements && (
              <div className={styles['table-wrapper']}>
                <table className={styles['improvement-table']}>
                  <thead>
                    <tr>
                      <th className={`${styles['table-header-cell']} ${styles['col-issue']}`}>
                        Issue
                      </th>
                      <th className={`${styles['table-header-cell']} ${styles['col-improve']}`}>
                        What to improve
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {areasOfImprovement.map((row, index) => {
                      const impactConfig = IMPACT_CONFIG[normalizeImpact(row.impact)];

                      return (
                        <tr key={index}>
                          <td className={`${styles['table-cell']} ${styles['col-issue']}`}>
                            <div className={styles['issue-cell']}>
                              <span className={styles['issue-text']}>
                                {row.issue}
                              </span>
                              <span className={styles['impact-badge']}>
                                <span
                                  className={styles['impact-dot']}
                                  style={{ backgroundColor: impactConfig?.color }}
                                />
                                <span className={styles['impact-text']}>
                                  {impactConfig?.label ?? row.impact}
                                </span>
                              </span>
                            </div>
                          </td>
                          <td className={`${styles['table-cell']} ${styles['table-cell-improve']}`}>
                            {row.what_to_improve}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {hasWhatsWorking && (
              <div>
                <h3 className={styles['section-heading']}>
                  What's working
                </h3>
                <ul className={styles['bullet-list']}>
                  {whatsWorking.map((item, index) => (
                    <li key={index} className={styles['bullet-item']}>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {primaryAudience && (
              <div>
                <p className={styles['section-heading']}>
                  Primary audience
                </p>
                <p className={styles['section-body']}>
                  {primaryAudience}
                </p>
              </div>
            )}
            {hasMetrics && (
              <div>
                <h3 className={styles['section-heading']}>
                  Primary metrics
                </h3>
                <p className={styles['section-body']}>
                  {primaryMetrics.map((metric, index) => (
                    <span key={index}>
                      {index > 0 && (
                        <span className={styles['metrics-separator']}>
                          |
                        </span>
                      )}
                      <span className={styles['metric-text']}>
                        {metric}
                      </span>
                    </span>
                  ))}
                </p>
              </div>
            )}
          </div>

          {!isExpanded && <div className={styles['blur-overlay']} />}
        </div>
      )}

      <div className={styles['footer-actions']}>
        {hasCollapsibleContent && (
          <button
            type="button"
            className={styles['toggle-btn']}
            onClick={() => setIsExpanded(prev => !prev)}
          >
            {isExpanded ? 'See less' : 'See more'}
          </button>
        )}
        <button
          type="button"
          className={styles['copy-btn']}
          onClick={handleCopy}
          aria-label={copied ? 'Copied' : 'Copy overview'}
        >
          <img
            src={copied ? checkGrayIcon : copyChatIcon}
            alt={copied ? 'Copied' : 'Copy'}
            className={styles['copy-icon']}
          />
        </button>
      </div>
    </div>
  );
};

OverviewReport.propTypes = {
  overview: PropTypes.shape({
    review_snippet: PropTypes.string,
    score_assessment: PropTypes.shape({
      score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    areas_of_improvement: PropTypes.arrayOf(PropTypes.shape({
      impact: PropTypes.string,
      issue: PropTypes.string,
      what_to_improve: PropTypes.string,
    })),
    whats_working: PropTypes.arrayOf(PropTypes.string),
    primary_audience: PropTypes.string,
    primary_metrics: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default OverviewReport;
